import { Injectable } from "@angular/core";
import { BaseNotifier } from "../../../shared/notifiers/base-notifier";
import { Ref } from "../../../shared/providers/ref";
import { DarkModeProvider } from "../../../shared/providers/dark-mode-provider";
import { GeoportalLayoutState } from "./geoportal-layout-state";

/**
 * GeoportalLayoutNotifier — dueño del estado del layout del geoportal.
 *
 * Sincroniza el tema (dark/light) con DarkModeProvider de forma reactiva
 * mediante ref.watch/listen. El toggle de tema se delega al notifier del
 * dark mode (que persiste y aplica la clase al documento).
 */
@Injectable()
export class GeoportalLayoutNotifier extends BaseNotifier<GeoportalLayoutState> {
  private static readonly INITIAL: GeoportalLayoutState = {
    ready: false,
    isDark: false,
    menuOpen: false,
  };

  constructor(ref: Ref) {
    super(ref, GeoportalLayoutNotifier.INITIAL);
  }

  override build(): void {
    this.setState(GeoportalLayoutNotifier.INITIAL);

    // Estado listo: el shell se monta cuando el layout terminó de inicializar.
    this.updateState((s) => ({ ...s, ready: true }));

    // Reacciona al tema global para reflejarlo en el toggle (sol/luna).
    this.listen(() => {
      const isDark = this.ref.watch(DarkModeProvider).state();
      this.updateState((s) => (s.isDark === isDark ? s : { ...s, isDark }));
    });
  }

  /** Alterna dark/light delegando en el notifier del dark mode. */
  toggleTheme(): void {
    this.ref.read(DarkModeProvider).notifier.toggle();
  }

  /** Alterna el menú móvil. */
  toggleMenu(): void {
    this.updateState((s) => ({ ...s, menuOpen: !s.menuOpen }));
  }

  /** Cierra el menú móvil. */
  closeMenu(): void {
    this.updateState((s) => (s.menuOpen ? { ...s, menuOpen: false } : s));
  }
}